import crypto from "crypto";
import {
  workCategoriesForApi,
  workCategoryCodesForItem,
  workCategoriesOrdered,
  type WorkCategoryCode,
} from "@/lib/content/work-categories";
import { LP_WORK_IDS } from "@/lib/content/lp-work-ids";
import {
  WORK_ITEMS,
  type WorkItem,
  workItemOnPublicSite,
} from "@/lib/content/work";
import {
  resolveWorkPublicationStatus,
  resolveWorkPortfolioTier,
  workItemVisibleInWorkApi,
  type WorkPortfolioTier,
  type WorkPublicationStatus,
} from "@/lib/content/work-publication";
import {
  getWorkIdByProjectCode,
  normalizeWorkProjectCode,
  resolveWorkProjectCode,
  type WorkProjectCode,
} from "@/lib/content/work-project-codes";
import { SITE_CONFIG } from "@/lib/constants/app.constants";
import {
  isAbsoluteMediaUrl,
  resolveWorkDeckMedia,
  resolveWorkMediaUrl,
  type WorkMedia,
} from "@/lib/content/work-media";

export type WorkApiScope = "public" | "full";

export type WorkItemApiRecord = {
  id: string;
  projectCode: WorkProjectCode;
  slug: string | null;
  title: string;
  summary: string;
  spotlightCopy?: string | string[];
  /** @deprecated Same value as `spotlightCopy` — kept for API backward compatibility. */
  oneLiner?: string | string[];
  featuredMedia: {
    type: "image" | "video";
    url: string;
    fullUrl?: string;
  };
  screenshot?: {
    type: "image" | "video";
    url: string;
    fullUrl?: string;
  };
  mediaAssets?: Array<{
    key: string;
    label?: string;
    type: "image" | "video";
    url: string;
    fullUrl?: string;
  }>;
  /** Key into `mediaAssets` for deck hero — omitted when unset (use `featuredMedia`). */
  deckMediaKey?: string;
  /** Resolved deck hero when `deckMediaKey` is set. */
  deckMedia?: {
    type: "image" | "video";
    url: string;
    fullUrl?: string;
  };
  color: string;
  client: {
    name: string;
    logo: string;
    website?: string;
    contact?: string;
  };
  categories?: string[];
  /** Every category on this item with slug, label, codeLetter, and serial flag. */
  categoryCodes?: WorkCategoryCode[];
  listed: boolean;
  campaignLpOnly: boolean;
  publicationStatus: WorkPublicationStatus;
  portfolioTier: WorkPortfolioTier;
  longform?: boolean;
  quote?: {
    text: string;
    author?: string;
    role?: string;
  };
  paths: {
    caseStudy: string | null;
    caseStudyUrl: string | null;
  };
};

export type WorkApiListMeta = {
  count: number;
  scope: WorkApiScope;
  lpWorkIds?: typeof LP_WORK_IDS;
  categories: ReturnType<typeof workCategoriesForApi>;
};

export type WorkApiListResponse = {
  items: WorkItemApiRecord[];
  meta: WorkApiListMeta;
};

const LP_KEYS = Object.keys(LP_WORK_IDS) as (keyof typeof LP_WORK_IDS)[];

export function isLpWorkIdsKey(
  value: string,
): value is keyof typeof LP_WORK_IDS {
  return LP_KEYS.includes(value as keyof typeof LP_WORK_IDS);
}

export function resolveWorkSpotlightCopy(
  item: Pick<WorkItem, "spotlightCopy" | "oneLiner">,
): string | string[] | undefined {
  return item.spotlightCopy ?? item.oneLiner;
}

export function workItemSummary(item: WorkItem): string {
  if (typeof item.description === "string") {
    return item.description;
  }
  const spotlight = resolveWorkSpotlightCopy(item);
  if (Array.isArray(spotlight)) {
    return spotlight[0] ?? item.title;
  }
  if (typeof spotlight === "string") {
    return spotlight;
  }
  return item.title;
}

function absoluteAssetUrl(path: string): string {
  if (isAbsoluteMediaUrl(path)) {
    return path;
  }
  return `${SITE_CONFIG.url}${path.startsWith("/") ? path : `/${path}`}`;
}

function serializeMedia(
  media: WorkMedia,
  variant: "preview" | "full" = "preview",
): WorkItemApiRecord["featuredMedia"] {
  const url = resolveWorkMediaUrl(media, variant);

  return {
    type: media.type,
    url: absoluteAssetUrl(url),
    ...(media.fullUrl ? { fullUrl: absoluteAssetUrl(media.fullUrl) } : {}),
  };
}

export function serializeWorkItem(
  item: WorkItem,
  scope: WorkApiScope = "public",
): WorkItemApiRecord {
  const mediaVariant = scope === "full" ? "full" : "preview";
  const caseStudyPath = item.slug ? `/work/${item.slug}` : null;
  const categoryCodes = workCategoryCodesForItem(item);
  const spotlightCopy = resolveWorkSpotlightCopy(item);
  const deckMedia = resolveWorkDeckMedia(item);

  return {
    id: item.id,
    projectCode: resolveWorkProjectCode(item),
    slug: item.slug,
    title: item.title,
    summary: workItemSummary(item),
    ...(spotlightCopy !== undefined
      ? { spotlightCopy, oneLiner: spotlightCopy }
      : {}),
    featuredMedia: serializeMedia(item.featuredMedia, mediaVariant),
    ...(item.screenshot
      ? { screenshot: serializeMedia(item.screenshot, mediaVariant) }
      : {}),
    ...(item.mediaAssets?.length
      ? {
          mediaAssets: item.mediaAssets.map((asset) => ({
            key: asset.key,
            ...(asset.label ? { label: asset.label } : {}),
            ...serializeMedia(asset.media, mediaVariant),
          })),
        }
      : {}),
    ...(item.deckMediaKey && deckMedia
      ? {
          deckMediaKey: item.deckMediaKey,
          deckMedia: serializeMedia(deckMedia, mediaVariant),
        }
      : {}),
    color: item.color,
    client: {
      name: item.client.name,
      logo: absoluteAssetUrl(item.client.logo),
      ...(item.client.website ? { website: item.client.website } : {}),
      ...(item.client.contact ? { contact: item.client.contact } : {}),
    },
    ...(item.categories
      ? { categories: workCategoriesOrdered(item.categories) }
      : {}),
    ...(categoryCodes.length ? { categoryCodes } : {}),
    listed: item.listed === true,
    campaignLpOnly: item.campaignLpOnly === true,
    publicationStatus: resolveWorkPublicationStatus(item),
    portfolioTier: resolveWorkPortfolioTier(item),
    ...(item.longform ? { longform: true } : {}),
    ...(item.quote
      ? {
          quote: {
            text: item.quote.text,
            ...(item.quote.author ? { author: item.quote.author } : {}),
            ...(item.quote.role ? { role: item.quote.role } : {}),
          },
        }
      : {}),
    paths: {
      caseStudy: caseStudyPath,
      caseStudyUrl: caseStudyPath ? absoluteAssetUrl(caseStudyPath) : null,
    },
  };
}

export function workApiHasFullAccess(request: Request): boolean {
  const token = process.env.WORK_API_TOKEN;
  if (!token) {
    return false;
  }

  const auth = request.headers.get("authorization");
  if (!auth?.startsWith("Bearer ")) {
    return false;
  }

  const provided = auth.slice("Bearer ".length);
  const expected = Buffer.from(token);
  const actual = Buffer.from(provided);

  if (actual.length !== expected.length) {
    return false;
  }

  return crypto.timingSafeEqual(actual, expected);
}

export function workApiScope(request: Request): WorkApiScope {
  return workApiHasFullAccess(request) ? "full" : "public";
}

function itemVisibleInScope(item: WorkItem, scope: WorkApiScope): boolean {
  return workItemVisibleInWorkApi(item, scope);
}

export type WorkApiQuery = {
  category?: string;
  lp?: keyof typeof LP_WORK_IDS;
  id?: string;
  slug?: string;
  projectCode?: string;
};

function resolveWorkApiQuery(query: WorkApiQuery): WorkApiQuery {
  if (query.id || !query.projectCode) {
    return query;
  }

  const code = normalizeWorkProjectCode(query.projectCode);
  const workId = code ? getWorkIdByProjectCode(code) : undefined;

  if (!workId) {
    return query;
  }

  const { projectCode, ...rest } = query;
  void projectCode;
  return { ...rest, id: workId };
}

export function listWorkForApi(
  scope: WorkApiScope,
  query: WorkApiQuery = {},
): WorkItemApiRecord[] {
  if (query.projectCode && !query.id) {
    const code = normalizeWorkProjectCode(query.projectCode);
    const workId = code ? getWorkIdByProjectCode(code) : undefined;
    if (!workId) {
      return [];
    }
  }

  const resolvedQuery = resolveWorkApiQuery(query);

  if (resolvedQuery.id) {
    const item = WORK_ITEMS.find((entry) => entry.id === resolvedQuery.id);
    if (!item || !itemVisibleInScope(item, scope)) {
      return [];
    }
    return [serializeWorkItem(item, scope)];
  }

  if (resolvedQuery.slug) {
    const item = WORK_ITEMS.find((entry) => entry.slug === resolvedQuery.slug);
    if (!item || !itemVisibleInScope(item, scope)) {
      return [];
    }
    return [serializeWorkItem(item, scope)];
  }

  if (resolvedQuery.lp) {
    const ids = LP_WORK_IDS[resolvedQuery.lp];
    return ids
      .map((id) => WORK_ITEMS.find((entry) => entry.id === id))
      .filter(
        (item): item is WorkItem =>
          item != null && itemVisibleInScope(item, scope),
      )
      .map((item) => serializeWorkItem(item, scope));
  }

  let items = WORK_ITEMS.filter((item) => itemVisibleInScope(item, scope));

  if (resolvedQuery.category && resolvedQuery.category !== "all") {
    items = items.filter((item) =>
      item.categories?.includes(resolvedQuery.category!),
    );
  }

  return items.map((item) => serializeWorkItem(item, scope));
}

export function workApiListResponse(
  request: Request,
  query: WorkApiQuery = {},
): WorkApiListResponse | { error: string; status: number } {
  const scope = workApiScope(request);

  if (query.lp && scope === "public") {
    const lpIds = LP_WORK_IDS[query.lp];
    const hasRestrictedItems = lpIds.some((id) => {
      const item = WORK_ITEMS.find((entry) => entry.id === id);
      return item != null && !workItemOnPublicSite(item);
    });

    if (hasRestrictedItems) {
      return {
        error: "Bearer token required for campaign-only work items",
        status: 401,
      };
    }
  }

  const items = listWorkForApi(scope, query);

  if ((query.id || query.slug || query.projectCode) && items.length === 0) {
    const requestedById = query.id
      ? WORK_ITEMS.find((entry) => entry.id === query.id)
      : undefined;
    const requestedBySlug = query.slug
      ? WORK_ITEMS.find((entry) => entry.slug === query.slug)
      : undefined;
    const requestedByCode =
      query.projectCode != null
        ? (() => {
            const code = normalizeWorkProjectCode(query.projectCode);
            if (!code) {
              return undefined;
            }
            const workId = getWorkIdByProjectCode(code);
            return workId
              ? WORK_ITEMS.find((entry) => entry.id === workId)
              : undefined;
          })()
        : undefined;
    const requested = requestedById ?? requestedBySlug ?? requestedByCode;

    if (requested && !itemVisibleInScope(requested, scope)) {
      return {
        error: "Bearer token required for this work item",
        status: 401,
      };
    }

    return {
      error: "Work item not found",
      status: 404,
    };
  }

  return {
    items,
    meta: {
      count: items.length,
      scope,
      categories: workCategoriesForApi(),
      ...(scope === "full" ? { lpWorkIds: LP_WORK_IDS } : {}),
    },
  };
}

export {
  WORK_CATEGORIES,
  WORK_CATEGORY_LABELS,
  workCategoriesForApi,
  workCategoryLabel,
  workCategoryCodesForItem,
  workCategoryTagLabel,
  workTagCategoryCodes,
} from "@/lib/content/work-categories";
export type {
  WorkCategoryCode,
  WorkCategorySlug,
  WorkTagCategoryCode,
} from "@/lib/content/work-categories";

export const WORK_API_CORS_HEADERS = {
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "GET, OPTIONS",
  "Access-Control-Allow-Headers": "Authorization, Content-Type",
} as const;
